/**
 * تالارِ بازی‌ها از پنل (Backend/internal/hall؛ GET /v1/hall?for=site) — فهرست‌ها، نامشان و ترتیبِ
 * بازی‌هایشان. `hall_default.json` همان seed.json ِ سرور است (آزمونِ Go یکسانی‌شان را نگه می‌دارد):
 * site/build.ts بخش‌های خانه را در HTML ِ ایستا از همین می‌سازد و site/live.js با پاسخِ زنده‌ی سرور
 * همان بخش‌ها را دوباره می‌چیند — آینه‌ی `siteSections` در live.js.
 */
export type HallKind = "online" | "shelf" | "soon" | "rooms" | "quick" | "top";

export type HallGame = {
  id: string;
  fa: string;
  en?: string;
  note?: string;
  blurb?: string;
  emoji?: string;
  color?: string;
  pip?: string;
  art?: string;
  soon?: boolean;
  hidden?: boolean;
  /** فقط در پاسخِ سرور: سرور میزِ آنلاینش را دارد / فقط اتاقِ دوستان. */
  online?: boolean;
  rooms?: boolean;
};

export type HallList = { id: string; fa: string; en?: string; kind: HallKind; where: "app" | "site" | "both"; games: string[] };

export type Hall = { version?: string; games: HallGame[]; lists: HallList[] };

export type SiteGroup = { id: string; title: string; ids: string[] };

/**
 * سه بخشِ بازی‌های خانه: آنلاین (یک فهرست)، آفلاین (قفسه‌ها) و به‌زودی. هر بازی فقط یک جا: بازی‌ای
 * که در ردیفِ آنلاین هست در قفسه‌ها تکرار نمی‌شود. گروهِ خالی نمی‌آید.
 * `isOnline`: سایت فقط بازی‌ای را در ردیفِ آنلاین می‌گذارد که واقعاً میزِ آنلاین دارد.
 */
export function siteSections(h: Hall, isOnline: (id: string) => boolean): { online: SiteGroup | null; offline: SiteGroup[]; soon: SiteGroup[] } {
  const defs = new Map(h.games.filter((g) => !g.hidden).map((g) => [g.id, g]));
  const lists = h.lists.filter((l) => l.where !== "app");
  const ready = (id: string) => defs.has(id) && !defs.get(id)!.soon;
  const on = lists.find((l) => l.kind === "online");
  const online = on ? { id: on.id, title: on.fa, ids: on.games.filter((id) => ready(id) && isOnline(id)) } : null;
  const taken = new Set(online?.ids ?? []);
  const offline = lists.filter((l) => l.kind === "shelf")
    .map((l) => ({ id: l.id, title: l.fa, ids: l.games.filter((id) => ready(id) && !taken.has(id)) }))
    .filter((g) => g.ids.length > 0);
  const soon = lists.filter((l) => l.kind === "soon")
    .map((l) => ({ id: l.id, title: l.fa, ids: l.games.filter((id) => defs.get(id)?.soon === true) }))
    .filter((g) => g.ids.length > 0);
  return { online, offline, soon };
}

/** جای رنگِ کارتِ «به‌زودی»: ترتیبِ عنوان‌های قفسه، بعد عنوان‌های به‌زودی‌ای که قفسه نیستند (مثلِ اپ). */
export function shelfOrder(h: Hall): string[] {
  const shelves = h.lists.filter((l) => l.kind === "shelf").map((l) => l.fa);
  const soon = h.lists.filter((l) => l.kind === "soon").map((l) => l.fa).filter((t) => !shelves.includes(t));
  return [...shelves, ...soon];
}

export type HubShelf = { id: string; title: string; note?: string; ids: string[] };

/** یادداشتِ زیرِ نامِ قفسه در /play — برای قفسه‌هایی که امروز داشتند. */
const HUB_NOTE: Record<string, string> = { board: "آنلاین با دوستان", puzzle: "مرحله‌ها و مسابقه با دوستان" };

/**
 * قفسه‌های /play: فقط بازی‌هایی که در مرورگر بازی می‌شوند (`live`)، دسته‌بندی‌شده با قفسه‌های تالار و
 * به ترتیبِ ردیفِ آنلاین (همان ترتیبی که خانه‌ی سایت نشان می‌دهد). بازیِ آنلاینی که در هیچ قفسه‌ای
 * نیست، آخر، زیرِ نامِ ردیفِ آنلاین.
 */
export function hubShelves(h: Hall, live: string[]): HubShelf[] {
  const lists = h.lists.filter((l) => l.where !== "app");
  const on = lists.find((l) => l.kind === "online");
  // بازیِ خاموشِ «تالارِ بازی‌ها» در پاسخِ سرور نیست: نه در قفسه، نه زیرِ ردیفِ آنلاین — وگرنه فهرستی که
  // همه‌ی بازی‌هایش خاموش‌اند با همان بازی‌ها برمی‌گشت (کاربر، ۲ نوامبر ۲۰۲۶).
  const shown = new Set(h.games.filter((g) => !g.hidden).map((g) => g.id));
  const order = [...(on?.games ?? []), ...live.filter((id) => !on?.games.includes(id))].filter((id) => live.includes(id) && shown.has(id));
  const out: HubShelf[] = [];
  const placed = new Set<string>();
  for (const l of lists.filter((x) => x.kind === "shelf")) {
    const ids = order.filter((id) => l.games.includes(id) && !placed.has(id));
    ids.forEach((id) => placed.add(id));
    if (ids.length) out.push({ id: l.id, title: l.fa.startsWith("بازی") ? l.fa : `بازی‌های ${l.fa}`, note: HUB_NOTE[l.id], ids });
  }
  const rest = order.filter((id) => !placed.has(id));
  if (rest.length) out.push({ id: on?.id ?? "online", title: on?.fa ?? "بازی‌های آنلاین", ids: rest });
  return out;
}
